import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { BroadcastFilters } from '../types';
import { AUDIENCE_OPTIONS } from '../types';

interface Plan { id: string; name: string; }

interface Props {
  filters: BroadcastFilters;
  onChange: (f: BroadcastFilters) => void;
}

export const AudienceFilter = ({ filters, onChange }: Props) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);

  useEffect(() => {
    supabase.from('plans').select('id,name').then(({ data }) => setPlans((data as Plan[]) ?? []));
  }, []);

  const estimateAudience = useCallback(async () => {
    setCounting(true);
    try {
      let query = supabase.from('profiles').select('id', { count: 'exact', head: true });

      if (filters.audience === 'plan' && filters.plan_id) {
        const { data: subs } = await supabase.from('subscriptions').select('user_id').eq('plan_id', filters.plan_id);
        const ids = (subs ?? []).map((s: { user_id: string }) => s.user_id);
        if (ids.length === 0) { setAudienceCount(0); setCounting(false); return; }
        query = supabase.from('profiles').select('id', { count: 'exact', head: true }).in('id', ids);
      } else if (filters.audience === 'status' && filters.subscription_status) {
        const statusMap: Record<string, string> = { active: 'active', trial: 'trialing', overdue: 'past_due', cancelled: 'cancelled' };
        const { data: subs } = await supabase.from('subscriptions').select('user_id').eq('status', statusMap[filters.subscription_status] ?? filters.subscription_status);
        const ids = (subs ?? []).map((s: { user_id: string }) => s.user_id);
        if (ids.length === 0) { setAudienceCount(0); setCounting(false); return; }
        query = supabase.from('profiles').select('id', { count: 'exact', head: true }).in('id', ids);
      } else if (filters.audience === 'inactive') {
        const days = filters.inactive_days ?? 30;
        const threshold = new Date(Date.now() - days * 86400_000).toISOString();
        query = supabase.from('profiles').select('id', { count: 'exact', head: true }).lt('updated_at', threshold);
      } else if (filters.audience === 'no_onboarding') {
        query = supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('onboarding_completed', false);
      } else if (filters.audience === 'with_goals') {
        const { data: goals } = await supabase.from('goals').select('user_id').limit(5000);
        const ids = [...new Set((goals ?? []).map((g: { user_id: string }) => g.user_id))];
        if (ids.length === 0) { setAudienceCount(0); setCounting(false); return; }
        query = supabase.from('profiles').select('id', { count: 'exact', head: true }).in('id', ids);
      } else if (filters.audience === 'without_goals') {
        const { data: goals } = await supabase.from('goals').select('user_id').limit(5000);
        const withGoals = [...new Set((goals ?? []).map((g: { user_id: string }) => g.user_id))];
        if (withGoals.length > 0) {
          query = supabase.from('profiles').select('id', { count: 'exact', head: true }).not('id', 'in', `(${withGoals.join(',')})`);
        }
      } else if (filters.audience === 'with_transactions') {
        const { data: txns } = await supabase.from('transactions').select('user_id').limit(5000);
        const ids = [...new Set((txns ?? []).map((t: { user_id: string }) => t.user_id))];
        if (ids.length === 0) { setAudienceCount(0); setCounting(false); return; }
        query = supabase.from('profiles').select('id', { count: 'exact', head: true }).in('id', ids);
      } else if (filters.audience === 'without_transactions') {
        const { data: txns } = await supabase.from('transactions').select('user_id').limit(5000);
        const withTxns = [...new Set((txns ?? []).map((t: { user_id: string }) => t.user_id))];
        if (withTxns.length > 0) {
          query = supabase.from('profiles').select('id', { count: 'exact', head: true }).not('id', 'in', `(${withTxns.join(',')})`);
        }
      }

      const { count } = await query;
      setAudienceCount(count ?? 0);
    } catch {
      setAudienceCount(null);
    }
    setCounting(false);
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(estimateAudience, 600);
    return () => clearTimeout(t);
  }, [estimateAudience]);

  const selected = AUDIENCE_OPTIONS.find((o) => o.value === filters.audience);

  return (
    <div className="space-y-4">
      {/* Audience estimate */}
      <div className="bg-violet-600/10 border border-violet-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 flex items-center justify-center">
          <i className="ri-user-received-2-line text-violet-300 text-[20px]" />
        </div>
        <div>
          <p className="text-[11px] text-white/30 uppercase tracking-wider font-600">Público estimado</p>
          {counting ? (
            <p className="text-[20px] font-700 text-violet-300"><i className="ri-loader-4-line animate-spin text-[16px]" /></p>
          ) : (
            <p className="text-[20px] font-700 text-violet-300">
              {audienceCount === null ? '—' : audienceCount.toLocaleString('pt-BR')}
              <span className="text-[13px] font-400 text-white/30 ml-1">destinatários</span>
            </p>
          )}
        </div>
      </div>

      {/* Audience type */}
      <div>
        <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-2 block">Segmentação</label>
        <div className="grid grid-cols-1 gap-1.5">
          {AUDIENCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ audience: opt.value })}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-pointer text-left ${
                filters.audience === opt.value
                  ? 'bg-violet-600/20 border-violet-500/40 text-white'
                  : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${opt.icon} text-[15px]`} />
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-600">{opt.label}</p>
                <p className="text-[11px] text-white/30">{opt.description}</p>
              </div>
              {filters.audience === opt.value && (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-violet-400 text-[12px]" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Conditional sub-filters */}
      {filters.audience === 'plan' && (
        <div>
          <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-1.5 block">Plano</label>
          <select
            value={filters.plan_id ?? ''}
            onChange={(e) => onChange({ ...filters, plan_id: e.target.value || undefined })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] focus:outline-none focus:border-violet-500/60 transition-colors"
          >
            <option value="">Selecione um plano</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      )}

      {filters.audience === 'status' && (
        <div>
          <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-1.5 block">Status</label>
          <select
            value={filters.subscription_status ?? ''}
            onChange={(e) => onChange({ ...filters, subscription_status: e.target.value as BroadcastFilters['subscription_status'] || undefined })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] focus:outline-none focus:border-violet-500/60 transition-colors"
          >
            <option value="">Selecione o status</option>
            <option value="active">Ativos</option>
            <option value="trial">Em trial</option>
            <option value="overdue">Inadimplentes</option>
            <option value="cancelled">Cancelados</option>
          </select>
        </div>
      )}

      {filters.audience === 'inactive' && (
        <div>
          <label className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-1.5 block">
            Sem acesso há pelo menos
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={365}
              value={filters.inactive_days ?? 30}
              onChange={(e) => onChange({ ...filters, inactive_days: parseInt(e.target.value) || 30 })}
              className="w-24 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] focus:outline-none focus:border-violet-500/60 transition-colors"
            />
            <span className="text-white/40 text-[13px]">dias</span>
          </div>
        </div>
      )}

      {selected && (
        <div className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 flex items-start gap-2">
          <i className="ri-information-line text-white/30 text-[14px] mt-0.5" />
          <p className="text-[12px] text-white/30">{selected.description}</p>
        </div>
      )}
    </div>
  );
};
